import React, { useContext, useEffect } from 'react';

import { SeverityLevel } from '@sentry/react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { TokenResult } from '@stripe/stripe-js';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { arrayOf, func, node, oneOfType } from 'prop-types';
import { useActionData, useNavigate, useNavigation, useParams, useSearchParams, useSubmit } from 'react-router-dom';

import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, CardContent, CircularProgress, IconButton, Typography, useMediaQuery } from '@mui/material';

import PHONE_NUMBERS from '../../../../constants/phone_numbers';
import { ActionFormContext } from '../../../../context/ActionForm';
import { AnalyticsContext } from '../../../../context/Analytics';
import { CustomerRequestContext } from '../../../../context/CustomerRequest';
import useAffiliate from '../../../../hooks/useAffiliate';
import useCustomerInfoSync from '../../../../hooks/useCustomerInfoSync';
import isQuoteGuaranteed from '../../../../utils/isQuoteGuaranteed';
import isQuoteLongDistance from '../../../../utils/isQuoteLongDistance';
import sentryCaptureException from '../../../../utils/logging/sentryCaptureException';
import serviceGroupHasService from '../../../../utils/serviceGroupHasService';
import useQuote from '../../../../utils/useQuote';
import Dialog from '../../Dialog';

dayjs.extend(utc);
dayjs.extend(tz);

const movingLaborServices = ['HOURLYLABOR', 'LOADING', 'LOADINGUNLOADING', 'UNLOADING'];
const addonServices = ['PACKINGSERVICE'];
const TRANSIT_SERVICE = 'TRANSIT';

const ActionButton = ({ children, onClick }) => {
  const { continueDisabled, queryClientIsFetching, navigationStateIsActive } = useContext(ActionFormContext);

  return (
    <Button
      type="submit"
      variant="contained"
      onClick={onClick}
      disabled={queryClientIsFetching || navigationStateIsActive || continueDisabled}
      startIcon={queryClientIsFetching || navigationStateIsActive ? <CircularProgress color="secondary" size={20} /> : null}
    >
      {children}
    </Button>
  );
};

ActionButton.propTypes = {
  children: oneOfType([node, arrayOf(node)]),
  onClick: func,
};

ActionButton.defaultProps = {
  children: null,
  onClick: () => {},
};

const CheckoutButton = () => {
  const { data: quoteData } = useQuote();

  const { formRef, setContinueDisabled, setErrors: setFormErrors } = useContext(ActionFormContext);
  const { customerInfo: sessionCustomerInfo } = useContext(CustomerRequestContext);
  const { track } = useContext(AnalyticsContext);
  const customerInfoSync = useCustomerInfoSync();

  const submit = useSubmit();
  const stripe = useStripe();
  const elements = useElements();

  const createStripeToken = (event) => {
    track('Checkout - Initialize Booking');
    if (!formRef.current.reportValidity()) {
      track('Checkout - Validation Error - Form missing inputs');
      // Form invalid (missing required inputs) - do not submit form
      return;
    }
    track('Checkout - Creating Stripe Token');
    setFormErrors([]);
    setContinueDisabled(true);
    customerInfoSync();
    event.preventDefault();
    const cardElement = elements.getElement(CardElement);
    const customerName = `${sessionCustomerInfo.customer.firstName} ${sessionCustomerInfo.customer.lastName}`;
    stripe
      .createToken(cardElement, {
        name: customerName,
      })
      .then((result: TokenResult) => {
        if (result.token) {
          track('Checkout - Stripe Token Success');
          const {
            token: { id: paymentMethodToken },
          } = result;
          const formData = new FormData(formRef.current);
          formData.append('paymentMethodToken', paymentMethodToken);
          formData.append('intent', 'checkout');
          track('Checkout - Creating Order');
          submit(formData, { method: 'post' });
        } else if (result.error) {
          track('Checkout - Error - Stripe Token Failure', { error: result?.error });
          setContinueDisabled(false);
          const newFormError = {
            source: 'stripe',
            displayControl: 'step',
            data: result.error,
            type: 'stripe_token_failure',
          };
          setFormErrors([newFormError]);
        }
      });
  };

  if (!quoteData) return null;

  return (
    <ActionButton onClick={createStripeToken}>{`Reserve now${isQuoteGuaranteed(quoteData) ? ' for $0' : ''}`}</ActionButton>
  );
};

const ContinueButton = ({ onClickNextStep }) => <ActionButton onClick={onClickNextStep}>Continue</ActionButton>;

ContinueButton.propTypes = {
  onClickNextStep: func.isRequired,
};

const FormActionBar = () => {
  const { customerInfo, setCustomerInfo } = useContext(CustomerRequestContext);
  const {
    formRef,
    actionBarAlert,
    setContinueDisabled,
    modalAlert,
    setModalAlert,
    errors: formErrors,
    setErrors: setFormErrors,
  } = useContext(ActionFormContext);
  const { track, identify } = useContext(AnalyticsContext);
  const isDesktop = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));

  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source');
  const utmContent = searchParams.get('utm_content');
  const truckParam = searchParams.get('truck');
  const utmCampaign = searchParams.get('utm_campaign');
  const utmMedium = searchParams.get('utm_medium');
  const clickid = searchParams.get('clickid');
  const gclid = searchParams.get('gclid');
  const msclkid = searchParams.get('msclkid');

  const enableAffiliateCobranding = utmSource && (utmContent?.includes('partnerships') || utmContent?.includes('affiliate'));

  // Only call Hygraph for affiliate when enabled with utm params
  const [affiliate]: any[] = useAffiliate({ affiliate: enableAffiliateCobranding ? utmSource : null });

  const { step } = useParams();

  const { data: quoteData } = useQuote();

  const { ok, error, data }: any = useActionData() ?? {};
  const submit = useSubmit();
  const navigate = useNavigate();

  const hasActionErrored = typeof ok !== 'undefined' && !ok;
  const isInvalidCardError = error === 'invalid_card';
  const isStripeGatewayMismatchError = error === 'stripe_gateway_mismatch';
  const isDuplicateLocationError = error?.detail === 'duplicate_locations';
  const isUnableToValidateLocationError = error?.detail?.includes('Unable to validate address');

  const { state } = useNavigation();

  const isActionLoadingOrSubmitting = state === 'loading' || state === 'submitting';

  const showActionBarAlert = isDesktop && !hasActionErrored;

  const hasService = (serviceGroup, serviceId) =>
    _.findIndex(serviceGroup?.services, (service: any) => service.service_id === serviceId) >= 0;

  const movingServiceGroup = quoteData?.service_groups?.find((sg) =>
    movingLaborServices.some((service) => hasService(sg, service)),
  );
  const laborService = movingServiceGroup?.services?.find((service) => movingLaborServices.includes(service.service_id));
  const transitService = movingServiceGroup?.services?.find((service) => service.service_id === TRANSIT_SERVICE);
  const quotedLaborWorkers = laborService?.quoted_workers;
  const quotedTrucks = transitService?.quoted_workers ?? 0;
  // Special case for Happy Robot - checkout only flow
  const hideCancel = step === 'checkout' && quoteData?.affiliate_id === 'HAPPYROBOT';

  const onClickNextStep = (event) => {
    event.preventDefault();
    const formData = new FormData(formRef.current);
    const intent = formData.get('intent')?.toString();
    // If form invalid - exit - do not continue
    if (!formRef.current.reportValidity()) {
      return;
    }

    let newCustomerInfo = { ...customerInfo };

    if (utmSource) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        utmSource,
      };
    }

    if (utmMedium) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        utmMedium,
      };
    }

    if (utmCampaign) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        utmCampaign,
      };
    }

    if (clickid) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        clickid,
      };
    }

    if (gclid) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        gclid,
      };
    }
    if (msclkid) {
      newCustomerInfo.marketingSource = {
        ...(newCustomerInfo.marketingSource ?? {}),
        msclkid,
      };
    }

    if (step === 'service') {
      track('Choose Service - Continue', {
        template: customerInfo?.selectedTemplate,
        service: customerInfo?.truck ? 'Truck and Movers' : 'Movers Only',
      });
    }
    if (step === 'location') {
      // Strip out any empty location objects before submitting
      newCustomerInfo = {
        ...newCustomerInfo,
        locations: newCustomerInfo?.locations?.filter((location) => !_.isEmpty(location)) ?? [],
      };
      setCustomerInfo(newCustomerInfo);
      formData.set('customerSessionData', JSON.stringify(newCustomerInfo));
      track('Locations - Continue', {
        truck: customerInfo?.truck,
        numLocations: customerInfo?.locations?.length,
        locations: customerInfo?.locations,
      });
      if (customerInfo?.customer && identify) {
        identify({
          firstName: customerInfo?.customer?.firstName,
          lastName: customerInfo?.customer?.lastName,
          phone: customerInfo?.customer?.phone,
          email: customerInfo?.customer?.email,
        });
      }
    }
    if (step === 'property') {
      track('Property Details - Continue', {
        locations:
          customerInfo?.locations?.map((location) => {
            const { propertyInfo, ...locationInfo } = location;
            return {
              ...locationInfo,
              ...propertyInfo,
            };
          }) ?? [],
      });
    }
    if (step === 'inventory') {
      track('Inventory Collection', {
        inventory: customerInfo?.inventory?.rooms,
      });
    }
    if (step === 'special_inventory') {
      track('Special Inventory Collection - Continue', {
        specialInventory: customerInfo?.inventory?.additionalItems,
      });
    }
    if (step === 'date') {
      track('Move Date - Continue', {
        date: customerInfo?.date,
      });
    }
    if (step === 'time') {
      track('Choose Time - Continue', {
        time: customerInfo?.time,
      });
    }
    if (step === 'date_time') {
      track('Date / Time - Continue', {
        date: customerInfo?.date,
        time: customerInfo?.time,
      });
    }
    if (step === 'labor_only_notice') {
      // If customer wants to be notified, enforce truck = true
      // Otherwise, enforce truck = false
      // Proxy step logic will allow truck = false to move forward in flow
      if (intent === 'submit-labor-only-market-lead') {
        setCustomerInfo({
          ...customerInfo,
          truck: true,
        });
        formData.set(
          'customerSessionData',
          JSON.stringify({
            ...customerInfo,
            truck: true,
          }),
        );
      } else {
        setCustomerInfo({
          ...customerInfo,
          truck: false,
        });
        formData.set(
          'customerSessionData',
          JSON.stringify({
            ...customerInfo,
            truck: false,
          }),
        );
      }
    }
    if (step === 'package' || step === 'hourly') {
      if (!intent) {
        formData.append('intent', 'next-step');
      }
      track('Accept Quote', {
        quoteHours: laborService?.quoted_hours,
        quotePros: quotedLaborWorkers,
        quoteAmount: quoteData?.totals?.total,
        quoteLongDistance: isQuoteLongDistance(quoteData),
        quoteGuaranteed: isQuoteGuaranteed(quoteData),
      });
    }
    if (step === 'flex') {
      const eventPayload = {
        originalRequestDate: customerInfo?.date,
        originalRequestTime: customerInfo?.time?.toString(),
        requestedDate: dayjs(movingServiceGroup.start_datetime).tz(movingServiceGroup.start_timezone).format('YYYY-MM-DD'),
        requestedTime: dayjs(movingServiceGroup.start_datetime).tz(movingServiceGroup.start_timezone).format('H'),
        confirmedDate: dayjs(formData.get('selectedDate').toString())
          .tz(movingServiceGroup.start_timezone)
          .format('YYYY-MM-DD'),
        confirmedTime: formData.get('selectedTime'),
      };
      const currentServiceGroupId = formData.get('flexServiceGroupId').toString();
      const currentServiceGroupStartDateTime = quoteData.service_groups.find(
        (sg) => sg.id === currentServiceGroupId,
      ).start_datetime;
      const selectedStartDateTime = formData.get('selectedFlexQuoteStartDateTime').toString();
      if (selectedStartDateTime && currentServiceGroupStartDateTime !== selectedStartDateTime) {
        formData.set('intent', 'apply-flex');
      }
      track('Confirm Move Datetime', eventPayload);
    }
    if (step === 'addons') {
      if (!intent) {
        formData.append('intent', 'next-step');
      }
      const productAddons = quoteData?.products?.map((product) => ({
        name: product.product_id,
        price: product.estimated_unit_price,
      }));

      const addonServiceGroups =
        quoteData?.service_groups?.filter((serviceGroup) =>
          addonServices.some((addonServiceId) => serviceGroupHasService(serviceGroup, addonServiceId)),
        ) ?? [];
      const serviceAddons = addonServiceGroups
        .map(({ services }) => [services.map((service) => ({ name: service.service_id, price: service.total_cost }))].flat())
        .flat();

      track('Add Ons Confirmed', {
        addons: [...productAddons, ...serviceAddons],
      });
    }
    submit(formData, { method: 'post' });
  };

  useEffect(() => {
    if (hasActionErrored && !isActionLoadingOrSubmitting) {
      // Indicates soft failure showing the error in the action bar
      // We want to log these to sentry for tracking
      // Errors like promo code not found not logged here because ok=true in those cases
      let sentryMessage = error?.message ?? 'Unknown Error';
      let severity: SeverityLevel = 'fatal';
      const extraErrorData: Record<string, any> = { ...data, error };

      track('Booking - Action Errored', { error, actionError: sentryMessage, isInvalidCardError });

      if (isInvalidCardError) {
        sentryMessage = 'Invalid Card Error';
        data.handled = true;
        severity = 'error';
        const newFormError = {
          source: 'stripe',
          displayControl: 'step',
          data: {
            message: 'Your card is declined or invalid.',
          },
          type: 'stripe_charge_failure',
        };
        // Filter form errors to remove existing errors so we don't duplicate
        const filteredFormErrors = formErrors.filter((formError) => formError.type !== 'stripe_charge_failure');
        setFormErrors([...filteredFormErrors, newFormError]);
      }

      if (isStripeGatewayMismatchError) {
        sentryMessage = 'Stripe Gateway Mismatch Error';
        data.handled = true;
        severity = 'warning';
        const newFormError = {
          source: 'stripe',
          displayControl: 'step',
          data: {
            message: 'Please re-enter your card details to complete your booking.',
          },
          type: 'stripe_gateway_mismatch',
        };
        const filteredFormErrors = formErrors.filter(
          (formError) => !['stripe_charge_failure', 'stripe_gateway_mismatch'].includes(formError.type),
        );
        setFormErrors([...filteredFormErrors, newFormError]);
      }

      if (isDuplicateLocationError) {
        sentryMessage = 'Duplicate Locations Error';
        severity = 'error';
        const filteredFormErrors = formErrors.filter((formError) => formError.type !== 'stripe_charge_failure');
        const newFormError = {
          source: 'api',
          displayControl: 'step',
          data: {
            message: 'Please enter unique unit numbers for duplicated locations using Apartment or Townhouse Property Type.',
          },
          type: 'duplicate_locations',
        };
        setFormErrors([...filteredFormErrors, newFormError]);
      }

      if (isUnableToValidateLocationError) {
        sentryMessage = 'Location Validation Error';
        severity = 'error';
        const filteredFormErrors = formErrors.filter((formError) => formError.type !== 'stripe_charge_failure');
        const newFormError = {
          source: 'api',
          displayControl: 'step',
          data: {
            message:
              'We are unable to validate one or more of your addresses, please double check that they have been entered correctly.',
          },
          type: 'location_validation',
        };
        setFormErrors([...filteredFormErrors, newFormError]);
      }
      setContinueDisabled(false);
      sentryCaptureException(sentryMessage, severity, extraErrorData);
    }
  }, [
    error,
    hasActionErrored,
    isInvalidCardError,
    isStripeGatewayMismatchError,
    isDuplicateLocationError,
    isActionLoadingOrSubmitting,
  ]);

  useEffect(() => {
    let newInfo = { ...customerInfo };

    // Use case for marketing quick links - effectively makes location the first step instead of service step
    // Marketing site uses links that have truck param prepopulated
    // Translate this to customer info
    switch (truckParam) {
      case '1':
        newInfo.truck = true;
        break;
      case '0':
        newInfo.truck = false;
        break;
      default:
        break;
    }

    if (data?.startingLocationTimezone) {
      newInfo.locations[0] = {
        ...newInfo.locations[0],
        timezone: data?.startingLocationTimezone,
      };
    }

    if (data?.mileage) {
      newInfo.metadata = {
        ...(newInfo.metadata ?? {}),
        mileage: data.mileage,
      };
    }

    if (data?.multiLDLaborOnly) {
      newInfo.metadata = {
        ...(newInfo.metadata ?? {}),
        multiLDLaborOnly: data.multiLDLaborOnly,
      };
    }

    if (data?.leadId) {
      newInfo = {
        ...newInfo,
        leadId: data.leadId,
      };
    }

    if (data?.leadRecordType) {
      newInfo = {
        ...newInfo,
        leadRecordType: data.leadRecordType,
      };
    }

    if (affiliate?.affiliateAccountId) {
      newInfo = {
        ...newInfo,
        affiliateAccountId: affiliate.affiliateAccountId,
      };
    } else {
      newInfo = {
        ...newInfo,
        affiliateAccountId: null,
      };
    }

    if (utmSource) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        utmSource,
      };
    }

    if (utmMedium) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        utmMedium,
      };
    }

    if (utmCampaign) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        utmCampaign,
      };
    }

    if (clickid) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        clickid,
      };
    }

    if (gclid) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        gclid,
      };
    }
    if (msclkid) {
      newInfo.marketingSource = {
        ...(newInfo.marketingSource ?? {}),
        msclkid,
      };
    }

    setCustomerInfo(newInfo);
  }, [data, affiliate, truckParam, utmSource, utmCampaign, utmMedium, clickid, gclid, msclkid]);

  return (
    <>
      {modalAlert ? (
        <Dialog open onClose={() => setModalAlert(null)}>
          <CardContent>{modalAlert}</CardContent>
        </Dialog>
      ) : null}
      <Box
        bgcolor="background.default"
        boxShadow="0px -5px 15px rgba(0, 0, 0, 0.1)"
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={3}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={{ xs: 88, md: 100 }}
          width={{ xs: '100%', md: 600, lg: 650 }}
          maxWidth={{ md: 600, lg: 650 }}
          mx="auto"
          px={{ xs: 2, md: 0 }}
        >
          {/* Hide back button using empty box to preserve flex spacing */}
          {hideCancel ? (
            <Box />
          ) : (
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack color="action" />
            </IconButton>
          )}
          {quotedTrucks >= 2 && quotedLaborWorkers < 5 ? (
            <Box maxWidth={400} mx={2}>
              <Alert severity="error" variant="standard">
                <Typography variant="body3">2 truck moves require 5 or more pros.</Typography>
              </Alert>
            </Box>
          ) : null}
          {!isInvalidCardError &&
          !isDuplicateLocationError &&
          !isUnableToValidateLocationError &&
          isDesktop &&
          hasActionErrored &&
          !isActionLoadingOrSubmitting ? (
            <Box maxWidth={400} mx={2}>
              <Alert severity="error" variant="standard">
                <Typography variant="body3">
                  {`We're experiencing an issue, please try again later or give us a call, ${PHONE_NUMBERS.CUSTOMER_SERVICE.replace(
                    /^(1-)/,
                    '',
                  )}.`}
                </Typography>
              </Alert>
            </Box>
          ) : null}
          {showActionBarAlert && actionBarAlert ? actionBarAlert : null}

          {step === 'checkout' ? <CheckoutButton /> : <ContinueButton onClickNextStep={onClickNextStep} />}
        </Box>
      </Box>
    </>
  );
};

export default FormActionBar;
